import { partAQuestions, partBQuestions, TOTAL_QUESTIONS, mixedQuestions } from '../data/assessmentQuestions';

const ARCHETYPES = ['visionary', 'shepherd', 'organizer', 'evangelist', 'teacher', 'servant'];

/**
 * Calculate assessment results from answers
 * @param {object} answers - { partA: { questionId: answer }, partB: { questionId: rating } }
 * @param {array} questions - Optional array of questions used (for API questions with different IDs)
 */
export function calculateAssessmentResults(answers, questions = null) {
  // Initialize scores
  const scores = {
    visionary: 0,
    shepherd: 0,
    organizer: 0,
    evangelist: 0,
    teacher: 0,
    servant: 0
  };

  // If questions are provided, use them; otherwise fall back to local questions
  if (questions && questions.length > 0) {
    // Calculate scores from the provided questions
    questions.forEach((question) => {
      if (question.type === "multiple-choice") {
        const answer = answers.partA[question.id];
        if (answer && question.options && question.options[answer]) {
          const archetype = question.options[answer].archetype;
          if (archetype && scores[archetype] !== undefined) {
            scores[archetype] += 1;
          }
        }
      } else if (question.type === "rating") {
        const rating = answers.partB[question.id];
        if (rating && question.archetype && scores[question.archetype] !== undefined) {
          scores[question.archetype] += rating;
        }
      }
    });
  } else {
    // Fall back to local questions
    // Calculate Part A scores (1 point per answer)
    partAQuestions.forEach((question) => {
      const answer = answers.partA[question.id];
      if (answer) {
        const archetype = question.options[answer].archetype;
        scores[archetype] += 1;
      }
    });

    // Calculate Part B scores (1-5 points per answer)
    partBQuestions.forEach((question) => {
      const rating = answers.partB[question.id];
      if (rating) {
        scores[question.archetype] += rating;
      }
    });
  }

  // Calculate percentages based on total score distribution
  const totalScore = Object.values(scores).reduce((sum, score) => sum + score, 0);

  const percentages = {
    visionary: 0,
    shepherd: 0,
    organizer: 0,
    evangelist: 0,
    teacher: 0,
    servant: 0
  };

  if (totalScore > 0) {
    ARCHETYPES.forEach((archetype) => {
      percentages[archetype] = Math.round((scores[archetype] / totalScore) * 100);
    });
  }

  // Find primary and secondary archetypes
  const sortedArchetypes = ARCHETYPES
    .map((archetype) => ({ archetype, score: scores[archetype] }))
    .sort((a, b) => b.score - a.score);

  const primary = sortedArchetypes[0].archetype;

  // Secondary is significant if it's at least 70% of primary score
  const secondary = sortedArchetypes[1].score >= sortedArchetypes[0].score * 0.7
    ? sortedArchetypes[1].archetype
    : null;

  return {
    scores,
    percentages,
    primary,
    secondary
  };
}

export function getProgressPercentage(currentStep) {
  if (currentStep.type === 'intro') return 0;
  if (currentStep.type === 'results') return 100;

  return Math.round(((currentStep.questionIndex + 1) / TOTAL_QUESTIONS) * 100);
}

export function getCurrentQuestionNumber(currentStep) {
  return currentStep.questionIndex + 1;
}
